import type { InjectionKey, Ref, ShallowRef, StyleValue } from 'vue';
import type { IconComponent } from '../internal';

type InputHeight = 'xs' | 's' | 'm' | 'l' | 'xl';
export const FONT_SIZE_LOOKUP = {
  xs: '0.75rem',
  s: '0.875rem',
  m: '1rem',
  l: '1.125rem',
  xl: '1.25rem',
} as const;

/** The `BaseProps` type is used by the `BaseTextProps` type and the `BaseCheckedProps` type. */
type BaseProps = {
  /** The unique identifier of the element in the DOM.
   *
   * Defaults to a generated identifier if there is a `NxLabel` or a `NxInputContainerItem` in the parent chain without an associated element. */
  id?: string;
};

/** The `BaseTextProps` type is used by the `NxTextarea` component and the `BaseTextInputProps` type. */
type BaseTextProps = BaseProps & {
  /** @deprecated Use `v-model` instead. */
  value?: never;
};

/** The `InlineIconProps` type is used by the the `BaseTextInputProps` type. */
type InlineIconProps = {
  /** The icon component to use. Only ever use components from the `@blpde/nx-icons` package. */
  component: IconComponent;
  /** The alignment of the icon. */
  align: 'start' | 'end';
  /** Whether the filled version of the icon should be used. Defaults to `false` */
  filled?: boolean;
  /** The color of the icon. Defaults to `'var(--nxt-border-strong)'`
   *
   * **The color gets modified to `'var(--nxt-brand-bg)'` on focus!** */
  color?: string;
  /** The size of the icon. Defaults to `'1.125em'` */
  size?: `${number}em`;
  /** Callback function for clicks on the given icon component. If specified, the cursor behavior of the icon changes from click-trough to clickable. Defaults to `undefined` */
  onClick?: (payload: PointerEvent) => void;
  /**
   * Callback for a non-primary button (`auxclick`) on a clickable icon — the
   * middle click of a link-like action ("Öffnen" in a new tab). Opt-in: an icon
   * without it ignores the middle button, so clearing or picking never fires by
   * accident. Only meaningful together with `onClick`.
   */
  onAuxClick?: (payload: MouseEvent) => void;
  /** Accessible label; shown as text in the overflow menu and used as the button's aria-label. */
  label?: string;
  /**
   * When set, the icon renders inside an `NxTooltip` showing this text on hover
   * (leading and trailing action icons). Use it to explain a clickable affordance
   * so the action stays clear months later (e.g. "Verknüpfung lösen"). Independent
   * of `label`, which supplies the short accessible name / aria-label.
   */
  tooltip?: string;
  /**
   * Explicitly classify the icon, overriding the default heuristic (an icon is
   * decorative when it has no `onClick`). Set `false` to force a **functional**
   * icon (rendered as a button — keeps reveal/overflow behavior and becomes an
   * inert disabled button when the field is disabled) even when it currently
   * has no `onClick`, e.g. because the field is disabled and the handler was
   * stripped. Set `true` to force a decorative icon. Defaults to inferring from
   * `onClick`.
   */
  deco?: boolean;
  /**
   * When `true`, this functional icon is hidden until the field is hovered or
   * focused, then fades in. It keeps its flex box so padding can hold the
   * value off the trailing chrome (divider / clear). Only meaningful for action
   * icons (clickable, or forced via `deco: false`); decorative icons are always
   * shown. Opt-in — defaults to `false`.
   */
  revealOnInteraction?: boolean;
  /**
   * Whether this action button is left out of the Tab order (`tabindex="-1"`).
   * Omitted ⇒ decided by where the field sits: in a form Tab goes from field to
   * field and skips every icon (mouse, and shortcuts such as F4, still reach
   * it); in an NxTable cell the icons stay Tab stops for the table's own cell
   * navigation (see `isInTableCell`). `true` / `false` force either way.
   */
  skipTab?: boolean;
  /**
   * Pixel width this trailing deco occupies, used to pad the value away from
   * the glyph. Defaults to the standard deco slot (`TRAILING_DECO_PX`, € / %).
   * ISO currency chips ("CHF") are wider and must set this so the amount
   * cannot run into the code.
   */
  occupyPx?: number;
};

/** The `BaseTextInputProps` type is used by the `NxPasswordInput` component, the `NxTextInput` component and the internal `InputProps` type within the `NxInput` component. */
type BaseTextInputProps = BaseTextProps & {
  /** Additional styling for the input wrapper element if necessary. Defaults to `undefined` */
  wrapperStyle?: StyleValue;
  /** The icons to show within the input element. Defaults to `undefined` */
  icons?: InlineIconProps[];
  /** The height of the input. Defaults to `'s'`. */
  height?: InputHeight;
  /**
   * Where clickable trailing icons (lookup, goto, clear) sit. `end` is the
   * default (right of the value, with deco). `start` puts them on the left so a
   * right-aligned value can share the display cell's right edge. Table cells
   * with `align="end"` inject this; an explicit prop wins.
   */
  actionsAlign?: 'start' | 'end';
};

/** The `AlignedTextInputProps` type is used by the `NxTextInput`, `NxNumberInput` and `NxCurrencyInput` components (and the internal `NxInput`, which renders the field). */
type AlignedTextInputProps = BaseTextInputProps & {
  /**
   * Where the value sits in the field. Defaults to `'start'`. `'end'` puts it
   * on the field's end edge, so figures line up down a column of form fields
   * (a sums block). Only the text moves: icons, deco (`€`, `%`) and actions
   * keep their places — `actionsAlign` moves those. An `NxTable` column with
   * `align="end"` already aligns its edit fields; no prop needed there.
   */
  align?: 'start' | 'end';
};

/** The `BaseCheckedProps` type is used by the `NxSwitch` component. */
type BaseCheckedProps = BaseProps & {
  /** @deprecated Use `v-model` instead. */
  checked?: never;
};

/** The internal `inputContainerInjection` is used by all `NxInputContainerItem` components within a `NxInputContainer` component */
const inputContainerInjectionKey = Symbol() as InjectionKey<{
  /** Whether all items of this container should be shown. Defaults to `false` */
  showMore: Ref<boolean>;
}>;

/** The internal `labelInjectionKey` can be used by `NxInput` component (this includes `NxTextInput` component and `NxPasswordInput` component), `NxTextarea` component, `NxSwitch` component and is provided either by a `NxLabel` component or a `NxInputContainerItem` component */
const labelInjectionKey = Symbol() as InjectionKey<{
  /** The unique identifier of the element associated with this label. */
  for: Ref<string | undefined>;
  /** Associates the given element with this label.
   *
   * **This function should only be called after verifying no element has been associated with this label yet!** */
  setFor: (elementId: string) => void;
}>;

type InputStatusState = 'idle' | 'loading' | 'success' | 'error';

/** Save-status descriptor provided by `NxLoadingWrapper` to a child input. */
type InputStatus = {
  state: InputStatusState;
  /** Shown in a tooltip on the error icon. Only relevant when state === 'error'. */
  errorMessage?: string;
  /** True while the success feedback is fading out before returning to idle. */
  fadeOut: boolean;
  /** Disable the input (during loading). */
  disabled?: boolean;
  /** Hide the input's text/value (used while a related field is loading). */
  textHidden?: boolean;
};

/**
 * The surrounding `NxLoadingWrapper`'s save status. A dialog (`NxModal`, `NxDrawer`, the panel of
 * an `NxPopover` via `LoadingStateBoundary`) provides `null` for its content: each dialog is its
 * own error context, so its inputs never take the status of the field the dialog was opened from.
 */
const loadingStateInjectionKey = Symbol() as InjectionKey<{
  status: ShallowRef<InputStatus>;
  /** How a successful save is presented. Defaults to the established icon. */
  successPresentation?: 'icon' | 'border';
  /** How a failed save is presented. Defaults to the established icon. */
  errorPresentation?: 'icon' | 'border';
  /**
   * Lets a child input tell the wrapper an input handles the status inline (vs. the absolute
   * fallback icon). Returns the unregister function; the child calls it before it unmounts or a
   * `<KeepAlive>` deactivates it, so the wrapper shows its own icon again once no registered input
   * is left. Kit inputs go through `useLoadingStateRegistration`.
   */
  register: () => () => void;
} | null>;

/** Table `align="end"` cells provide `'start'` so lookup/goto sit left of a right-aligned value. */
const inputActionsAlignInjectionKey = Symbol() as InjectionKey<Ref<'start' | 'end'>>;

/**
 * Body edit cells (`.nx-ghost-cell`) provide `true`: the field is plain cell
 * text (no input shell, no action padding). Filter/create rows stay form fields.
 */
const inputPlainCellInjectionKey = Symbol() as InjectionKey<Ref<boolean>>;

/**
 * Whether `el` sits in an NxTable cell (body, create or filter row). Read from
 * the DOM, not provide/inject: a modal or drawer opened from a cell teleports
 * out of it and is a form again. Forms and table cells differ in Tab: a form
 * field lets Tab go to the next field (icons skipped, autocomplete Tab native
 * on a closed list); a cell keeps icons as Tab stops and the committing Tab
 * the table navigation relies on.
 */
function isInTableCell(el: Element | null | undefined): boolean {
  return !!el?.closest('.td, .th');
}

/** Registration handle returned to an `NxInputContainer` on register. */
type StickyHandle = {
  update: (labelText: string, index: number) => void;
  unregister: () => void;
};

/** Entry projection consumed by `NxStickyContainerScrubber`. */
type StickyScrubberItem = {
  id: string;
  labelText: string;
  index: number;
  scrollIntoView: () => void;
};

const stickyContainerGroupInjectionKey = Symbol() as InjectionKey<{
  /** DOM-sorted projection of registered sections. Used by `NxStickyContainerScrubber`. */
  items: Readonly<Ref<StickyScrubberItem[]>>;
  /** Id of the section currently considered active by scroll position or last programmatic scrub. */
  activeId: Readonly<Ref<string | null>>;
  register: (sectionEl: HTMLElement, labelText: string, index: number) => StickyHandle;
}>;

export {
  isInTableCell,
  inputActionsAlignInjectionKey,
  inputPlainCellInjectionKey,
  inputContainerInjectionKey,
  labelInjectionKey,
  loadingStateInjectionKey,
  stickyContainerGroupInjectionKey,
};
export type {
  InputHeight,
  BaseCheckedProps,
  BaseTextInputProps,
  BaseTextProps,
  InlineIconProps,
  InputStatus,
  InputStatusState,
  StickyHandle,
  StickyScrubberItem,
  AlignedTextInputProps,
};
export * from '../internal';
